import React from 'react';
import LegalLayout, { Section } from '../components/legal/LegalLayout';

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" effectiveDate="April 24, 2026" testId="terms-page">
      <p>Welcome to VelvetGate. By using this platform, you agree to the following terms.</p>

      <Section n="1" title="Platform Role">
        <p>VelvetGate is a technology platform that enables event organizers (“Hosts”) to manage guest access, distribute passes, and communicate event details.</p>
        <p>VelvetGate is not the host, organizer, promoter, venue, or operator of events listed on the platform.</p>
      </Section>

      <Section n="2" title="Event Responsibility">
        <p>All events are created and managed by independent Hosts.</p>
        <p>VelvetGate is not responsible for:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Event quality, safety, or experience</li>
          <li>Accuracy of event details</li>
          <li>Changes, cancellations, postponements, or delays</li>
          <li>Access decisions made by Hosts</li>
          <li>Venue rules, security, or conduct policies</li>
        </ul>
      </Section>

      <Section n="3" title="Payments">
        <p>Payments for events may be handled outside of VelvetGate through payment methods selected by the Host, including but not limited to Venmo, Zelle, Cash App, PayPal, Stripe, or other payment services.</p>
        <p>Unless VelvetGate clearly states otherwise:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>VelvetGate does not process, hold, or manage event funds</li>
          <li>VelvetGate is not responsible for payment disputes between Guests and Hosts</li>
          <li>Proof of payment may be required by the Host before access is approved</li>
        </ul>
      </Section>

      <Section n="4" title="Access and Admission">
        <p>Event access is controlled by the Host.</p>
        <p>A pass may be:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Approved</li>
          <li>Pending approval</li>
          <li>Denied</li>
          <li>Revoked</li>
          <li>Marked as used</li>
        </ul>
        <p>VelvetGate is not responsible for admission decisions. A digital pass does not guarantee entry if the Host, venue, or event staff denies access.</p>
      </Section>

      <Section n="5" title="Refunds">
        <p>Unless explicitly stated by the Host, all payments are final.</p>
        <p>Refunds, credits, transfers, or cancellations are handled solely by the Host. VelvetGate is not responsible for issuing refunds unless VelvetGate directly processed the payment and clearly stated that refund handling applies.</p>
      </Section>

      <Section n="6" title="User Responsibility">
        <p>By using VelvetGate, you agree to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Provide accurate contact information</li>
          <li>Not misuse, duplicate, sell, or fraudulently share passes</li>
          <li>Respect event rules, venue rules, and staff instructions</li>
          <li>Use the platform only for lawful purposes</li>
          <li>Accept responsibility for any information you submit</li>
        </ul>
      </Section>

      <Section n="7" title="Pass Usage">
        <p>Each pass is unique and may only be used according to the event rules.</p>
        <p>Revoked, expired, duplicate, or already-used passes may be denied. Screenshots, forwarded links, printed copies, or duplicated QR codes may be rejected if they do not match the valid pass record.</p>
      </Section>

      <Section n="8" title="Platform Availability">
        <p>VelvetGate is provided on an “as is” and “as available” basis.</p>
        <p>We do our best to keep the platform reliable, but we do not guarantee uninterrupted service, error-free operation, or permanent availability of any feature.</p>
      </Section>

      <Section n="9" title="Limitation of Liability">
        <p>To the fullest extent allowed by law, VelvetGate is not liable for:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Denied entry</li>
          <li>Lost payments</li>
          <li>Event cancellations</li>
          <li>Host disputes</li>
          <li>Guest conduct</li>
          <li>Venue incidents</li>
          <li>Technical interruptions</li>
          <li>Lost, delayed, or undelivered communications</li>
        </ul>
      </Section>

      <Section n="10" title="Changes to Terms">
        <p>VelvetGate may update these terms at any time. Continued use of the platform means you accept the updated terms.</p>
      </Section>

      <Section n="11" title="Feature Access, Tiers, and Promotional Access">
        <p>VelvetGate offers platform features across membership tiers (e.g., <b>Starter</b>, <b>Pro</b>, <b>Premium</b>). Tier availability and feature sets may be added, modified, or removed over time.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Feature access may vary by tier. Features available at one tier may not be available at another.</li>
          <li>Promotional access (including trials, admin-granted access, and gifted access via redemption codes) is offered at VelvetGate&rsquo;s discretion and <b>may expire</b> after a stated duration.</li>
          <li>Upon expiration of promotional access, your account returns to its underlying membership tier automatically.</li>
          <li>VelvetGate reserves the right to modify feature availability, tier definitions, and promotional offers at any time.</li>
          <li>Redemption codes are single-bundle: each code carries its own limits on usage, expiry, and target tier as communicated at distribution.</li>
        </ul>
      </Section>

      <Section n="12" title="Contact">
        <p>For platform-related questions, contact: <a href="mailto:admin@velvetgatepass.com" className="text-[#D4AF37] hover:underline">admin@velvetgatepass.com</a></p>
      </Section>
    </LegalLayout>
  );
}
